import { useCallback, useEffect, useRef, useState, type FocusEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BADGE_MAX, BELL_ALERTS } from '@constants/limits';
import { ROUTES } from '@constants/routes';
import { ALERT_BELL as T, ALERT_TYPE_TEXT, SEVERITY_TEXT } from '@locales/id/alerts';
import { COMMON } from '@locales/id/messages';
import { alertPaths, api, errorText, needsActionTotal, SEVERITIES, type Alert, type AlertPage, type AlertSummary } from '@lib/api';
import { ago, dateTime, num } from '@lib/format';
import { useDismiss } from '@lib/hooks';
import { wiggle } from '@lib/utils/animate';
import { setTitleCount } from '@lib/utils/documentTitle';
import { setFaviconBadge } from '@lib/utils/favicon';
import { Icon } from '@ui';
import { useAlertSummary } from '@features/alerts';
import { useCriticalToasts } from './useCriticalToasts';

/**
 * The worst few that need action: critical first, then penting, then informasi, newest first within each. Asks only
 * the severities that have any (at most three small requests, together).
 */
async function fetchWorst(s: AlertSummary): Promise<Alert[]> {
  const asks = SEVERITIES.filter((sev) => s.needsAction[sev] > 0).map((sev) =>
    api<AlertPage>(alertPaths.list({ state: 'needs_action', severity: sev }, {}, Math.min(BELL_ALERTS, s.needsAction[sev]))).then((p) => p.items),
  );
  return (await Promise.all(asks)).flat().slice(0, BELL_ALERTS);
}

/** The badge's colour: the worst severity waiting. */
const badgeTone = (s: AlertSummary | null) => (!s ? '' : s.needsAction.critical ? 'is-critical' : s.needsAction.warning ? 'is-warning' : 'is-info');

/**
 * Bell in the header. The badge counts what NEEDS ACTION (taken alerts no longer count), coloured by the worst one
 * waiting; the tab title and favicon count the critical ones. The popover lists the worst few, opening each in the
 * Peringatan drawer, and "Lihat semua (n)"; with none waiting it says calmly how many are being handled. A rise in
 * the count swings the bell and pops the badge; a new critical alert also toasts (useCriticalToasts).
 */
export function NotificationBell() {
  const { data: summary, error: summaryError, retry: retrySummary } = useAlertSummary({ live: true });
  const total = needsActionTotal(summary);
  const critical = summary?.needsAction.critical ?? 0;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Alert[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname, loc.search]);
  useCriticalToasts();

  // the count went up while the page is open: swing the bell, pop the badge (the first answer only sets the baseline)
  const shown = useRef<number | null>(null);
  const icon = useRef<HTMLSpanElement>(null);
  const [pop, setPop] = useState(0);
  useEffect(() => {
    if (!summary) return;
    const before = shown.current;
    shown.current = total;
    if (before === null || total <= before) return;
    wiggle(icon.current);
    setPop((n) => n + 1);
  }, [summary, total]);
  // seen from another tab or across the room: "(2) Monitoring CNG Merah Putih" and a red badge on the tab icon
  useEffect(() => {
    setTitleCount(critical);
    void setFaviconBadge(critical);
  }, [critical]);
  useEffect(
    () => () => {
      setTitleCount(0);
      void setFaviconBadge(0);
    },
    [],
  );

  // the list while open, refetched whenever the counts change; only the newest answer counts
  const seq = useRef(0);
  const load = useCallback(() => {
    if (!summary) return;
    const my = ++seq.current;
    fetchWorst(summary).then(
      (d) => {
        if (my !== seq.current) return;
        setItems(d);
        setError(null);
      },
      (e) => my === seq.current && setError(errorText(e)),
    );
  }, [summary]);
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const close = useCallback((escaped: boolean) => {
    setOpen(false);
    if (escaped) wrap.current?.querySelector<HTMLButtonElement>('.bell')?.focus();
  }, []);
  useDismiss(wrap, open, close);
  // keyboard: tabbing out of the menu closes it
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (open && !wrap.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
  };
  const done = () => setOpen(false);
  const name = T.name(total);
  const waiting = items ?? [];

  return (
    <div className="menu-wrap" ref={wrap} onBlur={onBlur}>
      <button type="button" className={`bell ${total > 0 ? 'has-alerts' : ''}`} aria-label={name} title={name} aria-expanded={open} aria-controls={open ? 'bell-menu' : undefined} onClick={() => setOpen((o) => !o)}>
        <span ref={icon} className="bell-icon">
          <Icon name="bell" size={20} />
        </span>
        {total > 0 && (
          <span key={pop} className={`bell-count ${badgeTone(summary)} ${pop ? 'pop' : ''}`} aria-hidden="true">
            {total > BADGE_MAX ? `${BADGE_MAX}+` : total}
          </span>
        )}
      </button>
      {open && (
        // tabIndex -1: a press on its text keeps the focus inside (it would read as leaving the menu and close it)
        <div className="menu bell-menu" id="bell-menu" role="region" aria-label={T.head} tabIndex={-1}>
          <div className="bell-head">
            <b>{T.head}</b>
            {total > 0 && <span className="bell-head-count">{num(total)}</span>}
          </div>
          {!summary && summaryError ? (
            <div className="bell-state">
              <span>
                {T.failed} {summaryError}
              </span>
              <button type="button" className="btn btn-sm" onClick={retrySummary}>
                {COMMON.retry}
              </button>
            </div>
          ) : !summary || (total > 0 && items === null && !error) ? (
            <div className="bell-state" role="status">
              <span className="spinner" aria-hidden="true" /> {COMMON.loading}
            </div>
          ) : total > 0 && items === null ? (
            <div className="bell-state">
              <span>
                {T.failed} {error}
              </span>
              <button type="button" className="btn btn-sm" onClick={load}>
                {COMMON.retry}
              </button>
            </div>
          ) : total === 0 ? (
            <div className="bell-state bell-calm">
              <span className="bell-ok">
                <Icon name="check" size={16} />
              </span>
              <span>{summary.handling > 0 ? T.calmHandling(num(summary.handling)) : T.calm}</span>
            </div>
          ) : (
            <>
              {error && (
                <div className="bell-stale" role="status">
                  <span>{T.stale}</span>
                  <button type="button" className="btn btn-sm btn-ghost" onClick={load}>
                    {COMMON.retry}
                  </button>
                </div>
              )}
              <ul className="bell-list">
                {waiting.map((a) => (
                  <li key={a.id}>
                    <Link to={ROUTES.alert(a.id)} onClick={done}>
                      <span className={`bell-sev sev-${a.severity}`} aria-hidden="true" />
                      <span className="bell-text">
                        <span className="bell-title">{a.title}</span>
                        <span className="xs muted" title={dateTime(a.createdAt)}>
                          {SEVERITY_TEXT[a.severity]} · {ALERT_TYPE_TEXT[a.type] ?? a.type} · {ago(a.createdAt)}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="bell-foot">
            <Link to={ROUTES.alerts} onClick={done}>
              {total > 0 ? T.seeAll(num(total)) : T.seeList}
              <Icon name="next" size={16} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
